'use client'

import { useStoreI18n } from '@/store'
import { DatePicker } from '@/ui/components'
import { addDays, startOfToday } from 'date-fns'
import { memo, useState } from 'react'

interface Props {
  fromId?: string
  fromName: string
  fromLabel?: string
  fromValue?: Date
  fromPlaceholder?: string
  toId?: string
  toName: string
  toLabel?: string
  toValue?: Date
  toPlaceholder?: string
  disabled?: boolean
  className?: string
  inputClassName?: string
  onFromDateSelect?: (date: Date) => void
  onToDateSelect?: (date: Date) => void
  onFromDateClear?: () => void
  onToDateClear?: () => void
  addDaysAmount?: number
}

const FromToDatePicker = ({
  fromId,
  fromName,
  fromLabel,
  fromValue,
  fromPlaceholder,
  toId,
  toName,
  toLabel,
  toValue,
  toPlaceholder,
  disabled,
  className,
  onFromDateSelect,
  onToDateSelect,
  onFromDateClear,
  onToDateClear,
  inputClassName,
  addDaysAmount = 1
}: Props) => {
  const D = useStoreI18n.use.dictionary()

  const [selectedDateFrom, setSelectedDateFrom] = useState<Date | undefined>(
    fromValue
  )
  const [previousFromValue, setPreviousFromValue] = useState<Date | undefined>(
    fromValue
  )
  if (fromValue !== previousFromValue) {
    setPreviousFromValue(fromValue)
    setSelectedDateFrom(fromValue)
  }
  const [isDateToOpen, setIsDateToOpen] = useState(false)
  const minDateFrom = startOfToday()
  const minDateTo = selectedDateFrom
    ? addDays(selectedDateFrom, addDaysAmount)
    : toValue

  return (
    <>
      <DatePicker
        name={fromName}
        id={fromId || fromName}
        label={fromLabel || D['fe.bookingbar.dateFrom']}
        disabled={disabled}
        selected={fromValue}
        placeholder={fromPlaceholder || D['fe.bookingbar.selectValue']}
        minDate={minDateFrom}
        className={className}
        inputClassName={inputClassName}
        onDateSelect={(date) => {
          setSelectedDateFrom(date)
          onFromDateSelect?.(date)
          if (!disabled) {
            setIsDateToOpen(true)
          }
        }}
        onDateClear={() => {
          setSelectedDateFrom(undefined)
          onFromDateClear?.()
          onToDateClear?.()
        }}
      />
      <DatePicker
        key={selectedDateFrom?.toISOString() || 'date-to-default'}
        name={toName}
        id={toId || toName}
        className={className}
        selected={toValue}
        inputClassName={inputClassName}
        placeholder={toPlaceholder || D['fe.bookingbar.selectValue']}
        label={toLabel || D['fe.bookingbar.dateTo']}
        disabled={disabled || !selectedDateFrom}
        minDate={minDateTo}
        defaultMonth={selectedDateFrom}
        onDateSelect={(date) => {
          onToDateSelect?.(date)
        }}
        onDateClear={onToDateClear}
        open={isDateToOpen}
        onOpenChange={setIsDateToOpen}
      />
    </>
  )
}

FromToDatePicker.displayName = 'FromToDatePicker'

export default memo(FromToDatePicker)
